/**
 * What a lead's `source` value means, and where it points back to.
 *
 * `Lead.source` is a free-form column written by whatever created the record:
 * the register autofill, the public form bridge, an import, the site scanner,
 * the voicemail intake, or a person typing into the "add lead" dialog. Two
 * screens read it (the juhuklient board and the dashboard's donut) and both had
 * their own idea of which values are known, so the scanner's leads showed up as
 * the literal string "site-scanner" on one and were counted under a `scanner`
 * key that nothing ever writes on the other.
 *
 * One list now, and it is the list of values the platform actually writes -
 * verified against the four places that create a lead:
 *   - `crm-lead-bridge/leadBridge.service.ts`      -> "vorm"
 *   - `ariregister/scan-leads.routes.ts`           -> "site-scanner"
 *   - `crm-calls/lib/voicemail-intake.ts`          -> "voicemail"
 *   - `crm-siteaudit/lead.ts`                     -> "agendivalmidus" | "otsingumootori-audit"
 *                                                    | "vastavuse-audit"
 *   - the CRM's own new-lead dialog                -> whichever value the operator picks
 *
 * One of those stored values is an Estonian word rather than an English one.
 * That is history, not a choice: it is what sits in the column today, and
 * rewriting it would need a migration of every existing row. Values added since
 * are English (`voicemail`, `phone`), which is where the column is going.
 *
 * Anything else is shown verbatim. A missing key rendered as `leads.src.x` is
 * worse than the raw value it was meant to replace.
 */
import { scanReportPath } from "../crm/sections/prospecting/steps";

/** Every source value with a translation, plus the `unknown` the dashboard
 *  substitutes for a null column. */
export const LEAD_SOURCES = [
  "ariregister",
  "vorm",
  "chatbot",
  "käsitsi",
  "phone",
  "import",
  "site-scanner",
  "voicemail",
  "agendivalmidus",
  "otsingumootori-audit",
  "vastavuse-audit",
  "unknown",
] as const;

export type LeadSource = (typeof LEAD_SOURCES)[number];

const KNOWN = new Set<string>(LEAD_SOURCES);

/**
 * What the "add a lead" dialog offers, in the order it offers them.
 *
 * A subset, and deliberately so: the picker is a claim about where a record
 * came from, and three of the values above are claims only the platform is in a
 * position to make. `site-scanner` means a scan report exists and the badge
 * becomes a link to it, so an operator choosing it by hand builds a link to a
 * page that was never produced; `chatbot` and `unknown` are the same kind of
 * statement about something no person did.
 *
 * `phone` and `voicemail` are here because the phone is where most of these
 * records actually start. The voicemail intake already stamps `voicemail` on
 * the leads it creates on its own; the picker covers the other half of the same
 * situation - somebody rang, an operator answered, and the record is written by
 * hand afterwards. That could only be filed as a manual entry before, which
 * loses the one thing worth knowing about it: it came in over the phone.
 */
export const PICKABLE_LEAD_SOURCES = [
  "käsitsi",
  "phone",
  "voicemail",
  "vorm",
  "ariregister",
  "import",
] as const satisfies readonly LeadSource[];

/**
 * Older spellings of the same two origins, folded onto the current ones.
 *
 * Demo and imported rows carry `scanner` and `manual` - short forms from before
 * the scanner wrote its own leads. They mean exactly what the canonical values
 * mean, so they get the same label and the same link rather than two more
 * strings to translate and keep in step.
 */
const ALIASES: Record<string, string> = { scanner: "site-scanner", manual: "käsitsi" };

/** The stored value, reduced to the one this module reasons about. */
export function canonicalLeadSource(source: string): string {
  return ALIASES[source] ?? source;
}

/** The label for a source, or the raw value when nothing here recognises it. */
export function leadSourceLabel(source: string, t: (key: string) => string): string {
  const key = canonicalLeadSource(source);
  return KNOWN.has(key) ? t(`leads.src.${key}`) : source;
}

/**
 * Counts per stored value -> counts per origin, biggest first.
 *
 * The API groups by the raw column, so a base holding both spellings comes back
 * as two rows that now carry the SAME label - a ring with two "Site scanner"
 * slices reads as a rendering fault. Folding them is also the more truthful
 * answer to the question the chart asks, which is where leads come from and not
 * how the column happens to be spelled.
 */
export function mergeLeadSources<T extends { source: string; count: number }>(
  rows: T[],
): { source: string; count: number }[] {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const key = canonicalLeadSource(row.source);
    totals.set(key, (totals.get(key) ?? 0) + row.count);
  }
  return [...totals].map(([source, count]) => ({ source, count })).sort((a, b) => b.count - a.count);
}

/**
 * Where the record came from, as a place the operator can go and look.
 *
 * Only two sources have one, and both are worth the trip: a scanner lead has
 * the report that made it a lead, a register lead has the company's entry. The
 * rest are events, not documents - a form submission or a voicemail leaves no
 * page of its own that this record knows how to name.
 *
 * Returns null when the source has no destination OR when the record is missing
 * the value the destination needs, which is the common case for hand-typed
 * leads: the source says "ariregister" because somebody used the autofill, and
 * then the register code was cleared before saving.
 */
export function leadSourceRoute(
  source: string | null,
  ref: { regCode?: string | null; scanDomain?: string | null },
): string | null {
  const key = source ? canonicalLeadSource(source) : null;
  // The Skannid tab of Kliendiotsing, which opens `?url=` on arrival. The
  // stored source value stays "site-scanner": it names where the lead came
  // from, not an address, and rewriting it would take every existing row.
  if (key === "site-scanner" && ref.scanDomain) return scanReportPath(ref.scanDomain);
  if (key === "ariregister" && ref.regCode) return `/ariregister/${ref.regCode}`;
  // The two audit sources deliberately have none. An audit DOES produce a
  // shareable report, but its token lives on the `AeoCheck` row and never on the
  // lead - and a capability URL that leaks onto a list screen stops being one.
  // The Sissetulevad tab (`/prospects/inbound`) is where those rows are read
  // instead.
  return null;
}
